import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Button, TextField } from '../ui/index.js';

type AskResult = Awaited<ReturnType<typeof api.post<'/api/v1/butler/ask'>>>;

interface Exchange {
  readonly question: string;
  readonly result: AskResult;
}

/**
 * Asking Butler about your own mail.
 *
 * The citations are the feature. An answer about somebody's correspondence that they cannot check
 * is a claim a machine made about their life, so every answer here is followed by the messages it
 * was drawn from — and an answer drawn from nothing says so instead of inventing one.
 *
 * The exchange is kept in the page rather than reloaded from the server after each question: the
 * turns are already stored against the conversation, and re-fetching the whole history to append
 * one line would make the fast part of this screen wait for the slow part.
 */
export function ButlerScreen(): ReactNode {
  const { t, translator } = useI18n();
  const { activeWorkspace } = useAuth();

  const [question, setQuestion] = useState('');
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const tenantId = activeWorkspace?.id;

  const send = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const asked = question.trim();
      if (tenantId === undefined || asked === '') return;
      setBusy(true);
      setFailure(null);
      try {
        const result = await api.post(
          '/api/v1/butler/ask',
          {
            question: asked,
            ...(conversationId === null ? {} : { conversationId }),
          },
          undefined,
          { tenantId },
        );
        setConversationId(result.conversationId);
        setExchanges((current) => [...current, { question: asked, result }]);
        setQuestion('');
      } catch {
        setFailure(t('butler.failed'));
      } finally {
        setBusy(false);
      }
    },
    [tenantId, question, conversationId, t],
  );

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('butler.title')}</h1>
        <p className="greeting__summary">{t('butler.subtitle')}</p>
      </header>

      {exchanges.map((exchange, index) => (
        <section className="card" key={`${index}-${exchange.result.turnId}`}>
          <h2 className="card__title">{exchange.question}</h2>

          {exchange.result.status === 'answered' ? (
            <p className="empty__body">{exchange.result.answer}</p>
          ) : (
            /* A refusal is an answer, and it is shown as plainly as one. */
            <p className="mail-message__blocked">{t(`butler.status.${exchange.result.status}`)}</p>
          )}

          {exchange.result.cited.length > 0 && (
            <>
              <p className="today-list__meta">
                {t('butler.readFrom', {
                  cited: exchange.result.cited.length,
                  retrieved: exchange.result.retrievedCount,
                })}
              </p>
              <ul className="today-list">
                {exchange.result.cited.map((message) => (
                  <li className="today-list__item" key={message.messageId}>
                    <span className="today-list__subject">
                      {message.subject === '' ? t('mail.noSubject') : message.subject}
                    </span>
                    <span className="today-list__meta">
                      {message.from} · {translator.formatDate(new Date(message.receivedAt))}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      ))}

      <section className="card">
        <form onSubmit={(event) => void send(event)}>
          <TextField
            label={t('butler.label')}
            value={question}
            onChange={(event) => setQuestion(event.currentTarget.value)}
            placeholder={t('butler.placeholder')}
            hint={t('butler.hint')}
            disabled={busy}
          />
          {failure !== null && <p className="mail-message__blocked">{failure}</p>}
          <div className="mailbox-form__actions">
            <Button variant="primary" type="submit" disabled={busy || question.trim() === ''}>
              {busy ? t('butler.thinking') : t('butler.ask')}
            </Button>
            {exchanges.length > 0 && (
              <Button
                disabled={busy}
                onClick={() => {
                  setExchanges([]);
                  setConversationId(null);
                }}
              >
                {t('butler.startOver')}
              </Button>
            )}
          </div>
        </form>
      </section>
    </>
  );
}
